import { gql, useApolloClient } from '@apollo/client';
import { Fragment, useContext, useEffect, useState } from 'react';
import { ApplicationContext } from 'shared';
import { Button, Col, Divider, Form, Grid, Input, Loader, Message, Panel, PanelGroup, Row, toaster } from 'rsuite';
import { FORMAT, setHeaderTitle } from 'lib/env';
import { isMobile } from 'react-device-detect';
import orderBy from 'lodash/orderBy';
import { format, parseISO } from 'date-fns';
import { toTimeZone } from 'lib/date';
import { useGetToken } from 'lib/auth';
import { getAllFields } from 'lib/helpers/field';
import { useApi } from 'lib';
import { Document, Page, pdfjs } from 'react-pdf';
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker-2.12.313.js';

const PortalInvoiceView = () => {
  const client = useApolloClient();
  const api = useApi();
  const { showError } = useContext(ApplicationContext);
  const [invoice, setInvoice] = useState<any>(null);
  const tokenIsExpired = false; //(token as any).exp < new Date().getTime() / 1000;
  const token: any = useGetToken(true);
  const [fields, setFields] = useState<any>([]);
  const [comments, setComments] = useState<string>('');
  const [numPages, setNumPages] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfFile, setPdfFile] = useState(undefined);
  const [pdfError, setPdfError] = useState(undefined);

  useEffect(() => {
    (async function getFieldsData() {
      const result = await getAllFields(client);
      setFields(result);
    })();
  }, []);

  useEffect(() => {
    setHeaderTitle('Invoice');

    (async () => {
      // if (!tokenIsExpired) {
      const data = await client.query({
        query: gql`
          query invoice($guid: ID) {
            invoice(guid: $guid) {
              id
              guid
              applicationGroup
              customer {
                id
                displayName
                displayAddress
                email
              }
            }
          }
        `,
        variables: {
          guid: (token as any).data.resource
        }
      });

      try {
        const pdfBytes: any = await api.getPDF(`/invoices/download/${(token as any).data.resource}/pdf`, { responseType: 'blob' });
        setPdfFile(pdfBytes);
        setPdfLoading(false);
      } catch (err: any) {
        const reader = new FileReader();
        reader.addEventListener('loadend', (e: any) => {
          setPdfError(e.srcElement.result);
        });

        // Start reading the blob as text.
        if (err.response.data.constructor.name === 'Blob') {
          reader.readAsText(err.response.data);
        }
        setPdfLoading(false);
      }

      setInvoice(data.data.invoice);
    })();
  }, []);

  const handleUpdateComments = async (guid: string, comments: string) => {
    try {
      const response: any = await client.mutate({
        mutation: gql`
          mutation publicUpdateWorkOrderCustomerComments($guid: ID!, $comments: String!) {
            publicUpdateWorkOrderCustomerComments(
              guid: $guid
              comments: $comments
            ) {
              success
              code
              message
            }
          }
        `,
        variables: { guid, comments }
      });

      toaster.push(
        <Message type={response.data.publicUpdateWorkOrderCustomerComments.success ? 'success' : 'error'} showIcon closable>{response.data.publicUpdateWorkOrderCustomerComments.message}</Message>
      );
    } catch (err) {
      showError(err);
    }
  };

  const onInvoiceLoadSuccess = (props: any) => {
    setNumPages(props._pdfInfo.numPages);
  }

  return (
    <div className="App">
      {tokenIsExpired
        ? <Fragment>
          <Grid>
            <Row>
              <Col xs={24}>
                <br />
                <Message type="error">This link has expired, please contact our office to receive a new link.</Message>
              </Col>
            </Row>
          </Grid>
        </Fragment>
        : <Fragment>
          {invoice &&
            <Grid fluid={isMobile}>
              <Row>
                <Col xs={14} md={12}>
                  <h1>{invoice.customer.displayName}</h1>
                  <p><strong>{invoice.customer.displayAddress}</strong></p>
                  <p>{fields.find((f: any) => f.key === invoice.workType)?.title}</p>
                  <br />
                </Col>
                <Col xs={10} md={12} style={{ textAlign: 'right' }}>
                  <h1>{fields.find((f: any) => f.key === invoice.applicationGroup)?.title}</h1>
                </Col>
              </Row>
              <Row>
                <Col xs={24}>
                  {pdfLoading
                    ? <Loader content="Loading PDF" />
                    : <Fragment>
                      {pdfFile
                        ? <Document
                          error="Failed to load PDF file, ensure template and version are assigned to document."
                          options={{
                            withCredentials: true
                          }}
                          onLoadSuccess={onInvoiceLoadSuccess}
                          file={pdfFile}
                        >
                          {
                            Array.from(
                              new Array(numPages),
                              (el, index) => (
                                <div style={{ border: 'solid 1px #eee', marginBottom: '1rem' }} key={`page_${index + 1}`}>
                                  <Page pageNumber={index + 1} />
                                </div>
                              ),
                            )
                          }
                        </Document>
                        : <div>
                          <div>Failed to load PDF file:</div>
                          <Message type='error'><pre style={{ padding: 0, margin: 0, fontFamily: '"Helvetica Neue",Helvetica,Arial,sans-serif' }}>
                            <div dangerouslySetInnerHTML={{ __html: (pdfError || '').replace(/(U\+0009)/g, '<a href="https://google.com/search?q=$1" target="_blank" rel="nofollow">$1</a>') }} />
                          </pre></Message>
                        </div>
                      }
                    </Fragment>
                  }
                </Col>
              </Row>
            </Grid>
          }
        </Fragment>
      }
    </div>
  );
}

export default PortalInvoiceView;
